/**
 * OneDrive (D104): conexão da conta (admin, em Parâmetros do sistema), retorno do login da Microsoft e a pasta de cada
 * licitação, criada na captação e movida conforme o status. Os arquivos ficam no OneDrive; o Supabase guarda o necessário.
 */
import { useEffect, useRef, useState } from "react";
import { Title, useNotify, usePermissions, useRefresh } from "react-admin";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { Permissoes } from "../lib/provedores";
import { supabase } from "../lib/supabase";
import { formatarDataHora } from "../lib/formato";

interface Situacao {
  configurado: boolean;
  conectado: boolean;
  conta_nome?: string | null;
  conta_email?: string | null;
  raiz_caminho?: string | null;
  conectado_em?: string | null;
  raiz_padrao: string;
}

interface ResultadoSincronizacao {
  ok: boolean;
  caminho: string;
  url: string | null;
  enviados: number;
  falhas: string[];
  restantes: number;
}

/** Chama a Edge Function onedrive e devolve o corpo, com a mensagem do servidor quando houver erro. */
async function chamarOneDrive<T>(corpo: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T & { erro?: string }>("onedrive", { body: corpo });
  if (error || !data) {
    let mensagem = error?.message ?? "falha";
    const contexto = (error as { context?: Response } | null)?.context;
    if (contexto) mensagem = ((await contexto.json().catch(() => null)) as { erro?: string } | null)?.erro ?? mensagem;
    throw new Error(mensagem);
  }
  return data;
}

/** Se há conta conectada (consulta leve no banco, sem chamar a Microsoft). */
async function oneDriveConectado(): Promise<boolean> {
  const { data } = await supabase.rpc("onedrive_situacao");
  return Array.isArray(data) && data.length > 0;
}

// Uma sincronização por licitação de cada vez; pedido durante outra é repetido no fim (evita pasta ou arquivo em dobro).
const emAndamento = new Map<string, Promise<ResultadoSincronizacao | null>>();
const repetir = new Set<string>();

/**
 * Cria ou move a pasta da licitação e envia os arquivos pendentes (em lotes, até acabar).
 * Sem conta conectada, não faz nada (devolve null). Falha aqui nunca desfaz o que foi gravado no sistema.
 */
export function sincronizarOneDrive(licitacaoId: string): Promise<ResultadoSincronizacao | null> {
  const atual = emAndamento.get(licitacaoId);
  if (atual) {
    repetir.add(licitacaoId);
    return atual;
  }
  const tarefa = (async () => {
    try {
      if (!(await oneDriveConectado())) return null;
      let ultimo: ResultadoSincronizacao | null = null;
      let enviados = 0;
      const falhas: string[] = [];
      do {
        repetir.delete(licitacaoId);
        // Lotes: a função envia alguns arquivos por chamada; para quando acaba ou quando só restam falhas.
        for (let lote = 0; lote < 25; lote++) {
          ultimo = await chamarOneDrive<ResultadoSincronizacao>({ acao: "sincronizar", licitacao_id: licitacaoId });
          enviados += ultimo.enviados;
          falhas.push(...ultimo.falhas);
          if (ultimo.restantes === 0 || ultimo.enviados === 0) break;
        }
      } while (repetir.has(licitacaoId));
      return ultimo ? { ...ultimo, enviados, falhas } : null;
    } finally {
      emAndamento.delete(licitacaoId);
    }
  })();
  emAndamento.set(licitacaoId, tarefa);
  return tarefa;
}

/** Dispara a sincronização sem esperar (depois de anexar arquivos). Erros só aparecem na ficha. */
export function sincronizarOneDriveEmSegundoPlano(licitacaoId: string): void {
  sincronizarOneDrive(licitacaoId).catch(() => undefined);
}

/* ------------------------------ Ficha da licitação ------------------------------ */

/**
 * Link da pasta no cabeçalho da ficha. Sincroniza ao abrir e sempre que o status ou o resultado mudam
 * (a pasta muda de grupo). Mostra o erro, se houver, sem bloquear o uso da ficha.
 */
export function PastaOneDrive({ licitacaoId, status, chave, url }: { licitacaoId: string; status: string; chave: string; url: string | null }) {
  const refresh = useRefresh();
  const [estado, setEstado] = useState<"parado" | "sincronizando" | "erro">("parado");
  const [mensagem, setMensagem] = useState("");
  const [link, setLink] = useState(url);
  const ultimaChave = useRef("");

  useEffect(() => setLink(url), [url]);

  useEffect(() => {
    const atual = `${licitacaoId}|${status}|${chave}`;
    if (ultimaChave.current === atual) return;
    ultimaChave.current = atual;
    let ativo = true;
    setEstado("sincronizando");
    sincronizarOneDrive(licitacaoId)
      .then((r) => {
        if (!ativo) return;
        setEstado(r && r.falhas.length ? "erro" : "parado");
        setMensagem(r?.falhas.length ? `Arquivos não enviados: ${r.falhas.join("; ")}` : "");
        if (r?.url && r.url !== link) {
          setLink(r.url);
          refresh();
        }
      })
      .catch((e: Error) => {
        if (!ativo) return;
        setEstado("erro");
        setMensagem(e.message);
      });
    return () => {
      ativo = false;
    };
  }, [licitacaoId, status, chave]);

  if (estado === "sincronizando" && !link) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <CircularProgress size={10} /> Preparando a pasta no OneDrive…
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {link && (
        <Link href={link} target="_blank" rel="noopener noreferrer" variant="caption">
          Abrir pasta no OneDrive
        </Link>
      )}
      {estado === "sincronizando" && <CircularProgress size={10} aria-label="Sincronizando com o OneDrive" />}
      {estado === "erro" && (
        <Tooltip title={mensagem}>
          <Typography variant="caption" color="warning.main" sx={{ cursor: "help" }}>
            OneDrive: falha na sincronização
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
}

/* ------------------------------ Parâmetros do sistema ------------------------------ */

export function OneDriveParametro() {
  const notify = useNotify();
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";
  const [situacao, setSituacao] = useState<Situacao | null>(null);
  const [erro, setErro] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [raiz, setRaiz] = useState("");

  async function carregar() {
    try {
      const s = await chamarOneDrive<Situacao>({ acao: "situacao" });
      setSituacao(s);
      setRaiz(s.raiz_caminho ?? s.raiz_padrao);
      setErro("");
    } catch (e) {
      setErro((e as Error).message);
    }
  }
  useEffect(() => {
    void carregar();
  }, []);

  async function conectar() {
    setOcupado(true);
    try {
      const { url } = await chamarOneDrive<{ url: string }>({ acao: "autorizar" });
      window.location.assign(url);
    } catch (e) {
      notify(`Não foi possível iniciar a conexão: ${(e as Error).message}`, { type: "error" });
      setOcupado(false);
    }
  }

  async function desconectar() {
    setOcupado(true);
    try {
      await chamarOneDrive({ acao: "desconectar" });
      notify("OneDrive desconectado. As pastas e arquivos já criados continuam no OneDrive.", { type: "info" });
      await carregar();
    } catch (e) {
      notify(`Não foi possível desconectar: ${(e as Error).message}`, { type: "error" });
    }
    setOcupado(false);
  }

  async function trocarRaiz() {
    setOcupado(true);
    try {
      await chamarOneDrive({ acao: "raiz", caminho: raiz });
      notify("Pasta raiz alterada. As licitações passam a ir para a nova pasta quando forem sincronizadas.", { type: "success" });
      await carregar();
    } catch (e) {
      notify(`Não foi possível trocar a pasta: ${(e as Error).message}`, { type: "error" });
    }
    setOcupado(false);
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontSize: "1rem" }}>
          OneDrive
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cada licitação ganha uma pasta (SIGLA-MODALIDADE-NÚMERO-ANO) dentro da pasta raiz, separada por status e ano. Os arquivos anexados no sistema são
          copiados para lá. A sigla vem do cadastro do órgão.
        </Typography>
        {erro && <Alert severity="error">{erro}</Alert>}
        {!erro && !situacao && <CircularProgress size={20} />}
        {situacao && !situacao.configurado && (
          <Alert severity="warning">
            Integração não configurada: faltam os segredos ONEDRIVE_CLIENT_ID e ONEDRIVE_CLIENT_SECRET nas Edge Functions do Supabase.
          </Alert>
        )}
        {situacao?.configurado && !situacao.conectado && (
          <Stack spacing={1} alignItems="flex-start">
            <Typography variant="body2">Nenhuma conta conectada.</Typography>
            {admin ? (
              <Button variant="contained" onClick={() => void conectar()} disabled={ocupado}>
                Conectar OneDrive
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Peça ao administrador para conectar.
              </Typography>
            )}
          </Stack>
        )}
        {situacao?.conectado && (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Conectado a <strong>{situacao.conta_nome ?? "conta Microsoft"}</strong>
              {situacao.conta_email ? ` (${situacao.conta_email})` : ""}
              {situacao.conectado_em ? ` desde ${formatarDataHora(situacao.conectado_em)}` : ""}.
            </Typography>
            <TextField
              label="Pasta raiz das licitações"
              value={raiz}
              onChange={(e) => setRaiz(e.target.value)}
              fullWidth
              disabled={!admin || ocupado}
              helperText="Caminho a partir da raiz do OneDrive, separado por /. As pastas de cima precisam existir; a última é criada se faltar."
            />
            {admin && (
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => void trocarRaiz()} disabled={ocupado || !raiz.trim() || raiz.trim() === situacao.raiz_caminho}>
                  Gravar pasta raiz
                </Button>
                <Button variant="outlined" onClick={() => void conectar()} disabled={ocupado}>
                  Reconectar
                </Button>
                <Button color="error" onClick={() => void desconectar()} disabled={ocupado}>
                  Desconectar
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------ Retorno do login da Microsoft ------------------------------ */

export function OneDriveRetorno() {
  const [parametros] = useSearchParams();
  const navigate = useNavigate();
  const [mensagem, setMensagem] = useState<{ tipo: "info" | "success" | "error"; texto: string }>({ tipo: "info", texto: "Conectando o OneDrive…" });
  const feito = useRef(false);

  useEffect(() => {
    if (feito.current) return;
    feito.current = true;
    const erroMicrosoft = parametros.get("error_description") ?? parametros.get("error");
    const code = parametros.get("code");
    const state = parametros.get("state");
    if (erroMicrosoft || !code || !state) {
      setMensagem({ tipo: "error", texto: `A Microsoft não autorizou a conexão${erroMicrosoft ? `: ${erroMicrosoft}` : "."}` });
      return;
    }
    chamarOneDrive<{ conta: string | null; raiz: string }>({ acao: "conectar", code, state })
      .then((r) => {
        setMensagem({ tipo: "success", texto: `OneDrive conectado${r.conta ? ` (${r.conta})` : ""}. Pasta raiz: ${r.raiz}.` });
        window.setTimeout(() => navigate("/parametro_sistema"), 2500);
      })
      .catch((e: Error) => setMensagem({ tipo: "error", texto: e.message }));
  }, []);

  return (
    <Card sx={{ mt: 2 }}>
      <Title title="OneDrive" />
      <CardContent>
        <Alert severity={mensagem.tipo} icon={mensagem.tipo === "info" ? <CircularProgress size={18} /> : undefined}>
          {mensagem.texto}
        </Alert>
        {mensagem.tipo !== "info" && (
          <Button sx={{ mt: 2 }} onClick={() => navigate("/parametro_sistema")}>
            Ir para Parâmetros do sistema
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
